import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import {
  Mail, RefreshCw, Inbox, FileText, Clock, RotateCcw, Trash2,
  BookOpen, HelpCircle, ChevronDown, ChevronRight, Zap, Info,
} from 'lucide-react';

export default function EmailQueuePage({ accounts = [] }) {
  const [subTab, setSubTab] = useState('queue');

  const [inboxQueue, setInboxQueue] = useState([]);
  const [queueLoading, setQueueLoading] = useState(false);
  const [queueFilters, setQueueFilters] = useState({
    status: 'all',
    account_id: 'all',
  });


  const fetchInboxQueue = useCallback(async () => {
    setQueueLoading(true);
    try {
      const params = new URLSearchParams();
      if (queueFilters.status !== 'all') params.append('status', queueFilters.status);
      if (queueFilters.account_id !== 'all') params.append('account_id', queueFilters.account_id);
      const { data } = await api.get(`/super-admin/inbox-queue?${params.toString()}`);
      setInboxQueue(data);
    } catch {
      toast.error('Failed to load inbox queue');
    } finally {
      setQueueLoading(false);
    }
  }, [queueFilters]);

  useEffect(() => {
    if (subTab === 'queue') fetchInboxQueue();
  }, [subTab, fetchInboxQueue]);

  const handleReprocessQueue = async (queueId) => {
    try {
      await api.post(`/super-admin/inbox-queue/${queueId}/reprocess`);
      toast.success('Item marked for reprocessing');
      fetchInboxQueue();
    } catch {
      toast.error('Failed to reprocess item');
    }
  };

  const handleDeleteQueueItem = async (queueId) => {
    if (!window.confirm('Are you sure you want to delete this queue item?')) return;
    try {
      await api.delete(`/super-admin/inbox-queue/${queueId}`);
      toast.success('Queue item deleted');
      fetchInboxQueue();
    } catch {
      toast.error('Failed to delete item');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      failed: 'bg-red-100 text-red-800',
      completed: 'bg-green-100 text-green-800',
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (d) => d ? new Date(d).toLocaleString() : 'N/A';

  const SUB_TABS = [
    { id: 'queue', label: 'Email Queue', icon: Mail },
    { id: 'docs', label: 'Technical Docs', icon: BookOpen },
  ];

  return (
    <div className="space-y-6" data-testid="email-queue-page">
      {/* Sub-tab nav */}
      <div className="border border-border rounded-none bg-card">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-3">
            <Mail className="h-6 w-6 text-primary" />
            <div>
              <h2 className="text-xl font-bold">Email Queue</h2>
              <p className="text-sm text-muted-foreground">Inbound emails awaiting processing</p>
            </div>
          </div>
          {inboxQueue.length > 0 && (
            <Badge variant="secondary" className="rounded-none text-xs">{inboxQueue.length} item(s)</Badge>
          )}
        </div>
        <div className="flex border-b border-border">
          {SUB_TABS.map((tab) => (
            <button key={tab.id} onClick={() => setSubTab(tab.id)} data-testid={`eq-subtab-${tab.id}`}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${subTab === tab.id ? 'border-primary text-primary bg-muted/30' : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/20'}`}>
              <tab.icon className="h-4 w-4" />{tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* === QUEUE TAB === */}
      {subTab === 'queue' && (
        <div className="space-y-6">
          {/* Filters + Refresh */}
          <div className="flex items-center justify-between">
            <div className="flex gap-4">
              <div className="w-48">
                <Label className="text-xs uppercase tracking-wide font-medium mb-1 block">Organisation</Label>
                <Select value={queueFilters.account_id} onValueChange={(value) => setQueueFilters({ ...queueFilters, account_id: value })}>
                  <SelectTrigger className="rounded-none" data-testid="eq-org-filter">
                    <SelectValue placeholder="All Organisations" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Organisations</SelectItem>
                    {accounts.map((account) => (
                      <SelectItem key={account.id} value={account.id}>
                        {account.organisation_name || account.company_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-40">
                <Label className="text-xs uppercase tracking-wide font-medium mb-1 block">Status</Label>
                <Select value={queueFilters.status} onValueChange={(value) => setQueueFilters({ ...queueFilters, status: value })}>
                  <SelectTrigger className="rounded-none" data-testid="eq-status-filter">
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="processing">Processing</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button variant="outline" size="sm" className="rounded-none" onClick={fetchInboxQueue} disabled={queueLoading} data-testid="refresh-email-queue-btn">
              <RefreshCw className={`h-4 w-4 mr-2 ${queueLoading ? 'animate-spin' : ''}`} />Refresh
            </Button>
          </div>

          {/* Email Queue Table */}
          <div className="border border-border rounded-none bg-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="email-queue-table">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Received From</th>
                    <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Organisation</th>
                    <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Subject</th>
                    <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Attachments</th>
                    <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Date</th>
                    <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Status</th>
                    <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Reason</th>
                    <th className="text-left text-xs uppercase tracking-wide font-medium p-3 border-b border-border">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {queueLoading ? (
                    <tr><td colSpan={8} className="text-center p-8 text-muted-foreground"><RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />Loading queue...</td></tr>
                  ) : inboxQueue.length === 0 ? (
                    <tr><td colSpan={8} className="text-center p-8 text-muted-foreground"><Inbox className="h-8 w-8 mx-auto mb-2 opacity-50" /><p>No items in email queue</p><p className="text-xs mt-1">Completed items appear in Intake Hub</p></td></tr>
                  ) : (
                    inboxQueue.map((item) => (
                      <tr key={item.id} className="border-b border-border hover:bg-muted/30" data-testid={`eq-row-${item.id}`}>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <Mail className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm font-mono truncate max-w-[180px]" title={item.from_email}>{item.from_email || 'Unknown'}</span>
                          </div>
                        </td>
                        <td className="p-3"><span className="text-sm font-medium">{item.organisation_name || 'Unknown'}</span></td>
                        <td className="p-3"><span className="text-sm truncate max-w-[200px] block" title={item.subject}>{item.subject || 'No subject'}</span></td>
                        <td className="p-3">
                          <div className="flex items-center gap-1">
                            <FileText className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm">{item.attachment_count || 0} file(s)</span>
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-1">
                            <Clock className="h-4 w-4 text-muted-foreground" />
                            <span className="text-xs">{formatDate(item.received_at)}</span>
                          </div>
                        </td>
                        <td className="p-3">
                          <Badge className={`rounded-none text-xs uppercase ${getStatusBadge(item.status)}`}>{item.status}</Badge>
                        </td>
                        <td className="p-3">
                          <span className="text-xs text-muted-foreground truncate max-w-[150px] block" title={item.error_reason || item.errors?.join(', ')}>
                            {item.error_reason || item.errors?.join(', ') || '-'}
                          </span>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-1">
                            {item.status === 'failed' && (
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 rounded-none" onClick={() => handleReprocessQueue(item.id)} title="Reprocess" data-testid={`eq-reprocess-${item.id}`}>
                                <RotateCcw className="h-4 w-4" />
                              </Button>
                            )}
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 rounded-none text-destructive hover:text-destructive" onClick={() => handleDeleteQueueItem(item.id)} title="Delete" data-testid={`eq-delete-${item.id}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {inboxQueue.length > 0 && (
            <p className="text-xs text-muted-foreground">Showing {inboxQueue.length} item(s) - Completed items automatically move to Intake Hub</p>
          )}
        </div>
      )}

      {/* === TECHNICAL DOCS TAB === */}
      {subTab === 'docs' && (
        <div className="space-y-6">
          {/* Overview Cards */}
          <div className="border border-border rounded-none bg-card p-6">
            <div className="flex items-center gap-3 mb-6"><BookOpen className="h-5 w-5 text-primary" /><h3 className="font-semibold text-lg">Email Queue Overview</h3></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DocCard title="Inbound Email Pipeline" content="Emails sent to organisation-specific addresses (e.g., related-group.482@inbound.billportal.io) are routed by Mailgun to the BillPortal webhook. Each email creates an inbox_queue record, then attachments are uploaded to S3 and sent to SQS for Lambda processing — the same path as manual uploads." />
              <DocCard title="Email Routing &amp; Inbox IDs" content="Each organisation gets a unique inbox ID in format: {orgname}.{3digits}. The full email address is {inbox_id}@inbound.billportal.io. Mailgun route matches .*@inbound.billportal.io and forwards all emails to the BillPortal webhook." />
              <DocCard title="Status Lifecycle" content="Pending: Email received, awaiting attachment extraction. Processing: Attachments being extracted and uploaded to S3. Completed: All attachments extracted, parser_jobs created, sent to SQS. Failed: Error during extraction (view Reason column)." />
              <DocCard title="Error Handling" content="Failed emails retain the error reason. Common failures: no valid PDF/image attachments, unrecognised inbox ID, attachment too large, S3 upload failure. Failed items can be retried via Reprocess." />
            </div>
          </div>

          {/* Architecture & Workflow */}
          <div className="border border-border rounded-none bg-card p-6" data-testid="eq-architecture-section">
            <div className="flex items-center gap-3 mb-6"><Zap className="h-5 w-5 text-primary" /><h3 className="font-semibold text-lg">Architecture &amp; Workflow</h3></div>
            <div className="space-y-4">
              <div className="border border-border p-4 bg-muted/20">
                <h4 className="font-semibold mb-2">End-to-End Email Processing Workflow</h4>
                <div className="font-mono text-xs text-muted-foreground bg-muted/50 p-3 border border-border space-y-1">
                  <p>{'  '}Utility Company / Vendor</p>
                  <p>{'           '}|</p>
                  <p>{'           '}v</p>
                  <p>{'  '}Sends bill email to: {'{orgname}.{3digits}'}@inbound.billportal.io</p>
                  <p>{'           '}|</p>
                  <p>{'           '}v</p>
                  <p>{'  '}Mailgun (MX: mxa.mailgun.org / mxb.mailgun.org)</p>
                  <p>{'  '}Route: match_recipient(".*@inbound.billportal.io")</p>
                  <p>{'  '}Action: forward to BillPortal webhook</p>
                  <p>{'           '}|</p>
                  <p>{'           '}v</p>
                  <p>{'  '}POST /api/inbound-email/webhook  (BillPortal Backend)</p>
                  <p>{'  '}  - Verify Mailgun signature (HMAC-SHA256)</p>
                  <p>{'  '}  - Parse recipient to extract inbox_id</p>
                  <p>{'  '}  - Match inbox_id to organisation (MongoDB: accounts)</p>
                  <p>{'           '}|</p>
                  <p>{'           '}v</p>
                  <p>{'  '}inbox_queue record created (status=pending)</p>
                  <p>{'           '}|</p>
                  <p>{'           '}v</p>
                  <p>{'  '}Attachment Extraction (background task)</p>
                  <p>{'  '}  - PDFs/images uploaded to S3</p>
                  <p>{'  '}  - document record created per attachment</p>
                  <p>{'  '}  - parser_jobs record created (status=queued)</p>
                  <p>{'  '}  - Sent to SQS (billportal-processing-queue)</p>
                  <p>{'           '}|</p>
                  <p>{'           '}v</p>
                  <p>{'  '}Lambda processes (OCR + AI extraction)</p>
                  <p>{'  '}parser_jobs updated (completed/failed)</p>
                  <p>{'  '}inbox_queue status updated to completed</p>
                  <p>{'           '}|</p>
                  <p>{'           '}v</p>
                  <p>{'  '}Parsed bills visible in Intake Hub for review</p>
                </div>
              </div>
              <div className="border border-border p-4 bg-muted/20">
                <h4 className="font-semibold mb-2">Component Architecture</h4>
                <div className="font-mono text-xs text-muted-foreground bg-muted/50 p-3 border border-border space-y-1">
                  <p>+---------------------------+     +---------------------------+</p>
                  <p>|  Mailgun Inbound Routes   | --&gt; |  BillPortal Webhook       |</p>
                  <p>|  inbound.billportal.io    |     |  /api/inbound-email/      |</p>
                  <p>|  MX: mxa/mxb.mailgun.org  |     |  webhook                  |</p>
                  <p>+---------------------------+     +---------------------------+</p>
                  <p>{'                                          '}|</p>
                  <p>{'                           '}+-----------+-----------+</p>
                  <p>{'                           '}|                       |</p>
                  <p>{'                           '}v                       v</p>
                  <p>{'                  '}+----------------+   +------------------+</p>
                  <p>{'                  '}| MongoDB         |   | S3 Storage       |</p>
                  <p>{'                  '}| inbox_queue     |   | Bill attachments |</p>
                  <p>{'                  '}| documents       |   | (PDFs, images)   |</p>
                  <p>{'                  '}+----------------+   +------------------+</p>
                  <p>{'                           '}|                       |</p>
                  <p>{'                           '}v                       v</p>
                  <p>{'                  '}+----------------+   +------------------+</p>
                  <p>{'                  '}| parser_jobs    |   | SQS Queue        |</p>
                  <p>{'                  '}| (status track) |   | --&gt; Lambda       |</p>
                  <p>{'                  '}+----------------+   +------------------+</p>
                </div>
              </div>
              <div className="border border-border p-4 bg-muted/20">
                <h4 className="font-semibold mb-2">Mailgun Configuration</h4>
                <div className="font-mono text-xs text-muted-foreground bg-muted/50 p-3 border border-border space-y-1">
                  <p>Inbound Domain:     inbound.billportal.io</p>
                  <p>Email Format:       {'{orgname}.{3digits}'}@inbound.billportal.io</p>
                  <p>Webhook URL:        {'{APP_URL}'}/api/inbound-email/webhook</p>
                  <p>Webhook Signature:  HMAC-SHA256 (Mailgun webhook signing key)</p>
                  <p>Mailgun Route:      match_recipient(".*@inbound.billportal.io")</p>
                  <p>Mailgun Action:     forward("{'{APP_URL}'}/api/inbound-email/webhook")</p>
                  <p>Config location:    Super Admin {'>'} Email Config tab</p>
                </div>
              </div>
            </div>
          </div>

          {/* Code References */}
          <div className="border border-border rounded-none bg-card p-6" data-testid="eq-code-references">
            <div className="flex items-center gap-3 mb-6"><Info className="h-5 w-5 text-primary" /><h3 className="font-semibold text-lg">Code References &amp; API</h3></div>
            <div className="space-y-4">
              <div className="border border-border p-4 bg-muted/20">
                <h4 className="font-semibold mb-2">Backend Files</h4>
                <div className="font-mono text-xs text-muted-foreground bg-muted/50 p-3 border border-border space-y-2">
                  <p className="text-foreground font-semibold">server.py — Webhook &amp; Queue Endpoints</p>
                  <p>{'  '}POST /api/inbound-email/webhook         — Main Mailgun/SendGrid webhook</p>
                  <p>{'  '}POST /api/inbound-email/{'{inbox_id}'}     — Direct per-org webhook</p>
                  <p>{'  '}process_inbox_attachments()              — Background task: extract + S3 + SQS</p>
                  <p>{'  '}create_document_from_content()           — S3 upload + parser_job + SQS send</p>
                  <p>{'  '}generate_inbox_id()                      — Auto-generate org inbox ID</p>
                  <p>{'  '}verify_mailgun_signature()               — HMAC-SHA256 signature check</p>
                  <p></p>
                  <p className="text-foreground font-semibold">server.py — Admin Endpoints</p>
                  <p>{'  '}GET  /api/super-admin/inbox-queue        — List queue items (filterable)</p>
                  <p>{'  '}POST /api/super-admin/inbox-queue/:id/reprocess — Retry processing</p>
                  <p>{'  '}DELETE /api/super-admin/inbox-queue/:id  — Remove queue item</p>
                  <p>{'  '}PUT  /api/super-admin/config/email       — Email service config</p>
                </div>
              </div>
              <div className="border border-border p-4 bg-muted/20">
                <h4 className="font-semibold mb-2">Frontend Files</h4>
                <div className="font-mono text-xs text-muted-foreground bg-muted/50 p-3 border border-border space-y-2">
                  <p className="text-foreground font-semibold">pages/cockpit/EmailQueuePage.js</p>
                  <p>{'  '}fetchInboxQueue()        — Load queue items with filters</p>
                  <p>{'  '}handleReprocessQueue()   — Retry failed email processing</p>
                  <p>{'  '}handleDeleteQueueItem()  — Remove item from queue</p>
                  <p></p>
                  <p className="text-foreground font-semibold">pages/SuperAdmin.js — Email Config tab</p>
                  <p>{'  '}Mailgun API key, webhook signing key, domain setup</p>
                  <p>{'  '}Setup instructions with MX records and route config</p>
                </div>
              </div>
              <div className="border border-border p-4 bg-muted/20">
                <h4 className="font-semibold mb-2">Collections</h4>
                <div className="font-mono text-xs text-muted-foreground bg-muted/50 p-3 border border-border space-y-1">
                  <p>inbox_queue      — Email queue records (id, account_id, from_email, subject,</p>
                  <p>{'                  '}status, attachment_count, documents_created, errors, received_at)</p>
                  <p>accounts         — Org config with inbox_id field for email routing</p>
                  <p>documents        — Attachment metadata (S3 key, account_id, filename, source)</p>
                  <p>parser_jobs      — Downstream parsing jobs per attachment</p>
                  <p>global_config    — Platform-wide email config (Mailgun keys, domain)</p>
                </div>
              </div>
            </div>
          </div>

          {/* FAQ */}
          <div className="border border-border rounded-none bg-card p-6" data-testid="eq-faq-section">
            <div className="flex items-center gap-3 mb-6"><HelpCircle className="h-5 w-5 text-primary" /><h3 className="font-semibold text-lg">FAQ</h3></div>
            <div className="space-y-4">
              <FaqItem q="How does an email get into this queue?" a="When a utility bill email is sent to an organisation's inbound address (e.g., related-group.482@inbound.billportal.io), Mailgun routes it to the BillPortal webhook. The webhook parses the recipient address to identify the org, creates an inbox_queue record, extracts attachments, uploads to S3, creates parser_jobs, and sends to SQS." />
              <FaqItem q="What is the email address format?" a="Each organisation gets a unique inbox ID in format: {orgname}.{3digits}@inbound.billportal.io. For example, 'related-group.482@inbound.billportal.io'. The inbox ID is auto-generated when an org is created and can be found in the org's Intake Hub settings." />
              <FaqItem q="What happens to completed emails?" a="Successfully processed emails stay in the queue with 'completed' status. Their extracted attachments flow through the Parser Queue (SQS → Lambda) and appear in the Intake Hub for review." />
              <FaqItem q="Why did an email fail?" a="Check the 'Reason' column. Common causes: no PDF attachments found, unrecognised inbox ID (no matching organisation), attachment too large, S3 upload failure, or Mailgun signature verification failure." />
              <FaqItem q="How do I reprocess a failed email?" a="Click the circular arrow (Reprocess) icon in the Actions column. This re-triggers the attachment extraction pipeline for that email." />
              <FaqItem q="Where is the email service configured?" a="Super Admin > Email Config tab. Set the Mailgun API key, webhook signing key, and inbound domain (inbound.billportal.io). The Mailgun route must forward .*@inbound.billportal.io to the BillPortal webhook URL." />
              <FaqItem q="How does email processing connect to the Parser Queue?" a="Each email attachment creates a document + parser_job record. The document is sent to the same SQS queue as manual uploads. You can trace from Email Queue (inbox_queue) → Parser Queue (parser_jobs) via the document_id. Filter Parser Queue by source=email to see email-originated jobs." />
            </div>
          </div>
        </div>
      )}

      {/* Docs Drawer removed — all docs consolidated into Technical Docs tab above */}
    </div>
  );
}

function DocCard({ title, content }) {
  return (<div className="border border-border p-4 bg-muted/20"><h4 className="font-semibold text-sm mb-2">{title}</h4><p className="text-xs text-muted-foreground leading-relaxed">{content}</p></div>);
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (<div className="border border-border"><button className="w-full p-4 text-left flex items-center justify-between hover:bg-muted/20 transition-colors" onClick={() => setOpen(!open)}><span className="font-medium text-sm">{q}</span>{open ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}</button>{open && <div className="px-4 pb-4 text-sm text-muted-foreground leading-relaxed border-t border-border pt-3">{a}</div>}</div>);
}
